import { Injectable } from '@angular/core';
import { LatLng, LeafletMouseEvent, Map, Marker, Point } from 'leaflet';
import { filter, map, Observable } from 'rxjs';
import { DMSPipe, MGAPipe } from '../../pipes';
import { createMarker } from '../map.model';
import { SelectMode } from './select.mode';
import { MapModes } from './shared';

@Injectable()
export class SelectCoordsMode extends SelectMode {
  private currentMarker: Marker | null = null;

  override type = MapModes.SELECT_COORDS;

  override handleClick = (event: LeafletMouseEvent): void => {
    if (event.originalEvent.target !== event.target.getContainer()) {
      return;
    }

    this.zone.run(() => {
      this.addOrReplaceCoordinatesMarker(event.latlng);
      this.mapClick$.next({ event, map: this.map });
    });
  };

  private addOrReplaceCoordinatesMarker(latlng: LatLng): void {
    this.removeCoordinatesMarker();
    this.currentMarker = createMarker(latlng).addTo(this.map);
  }

  removeCoordinatesMarker(): void {
    if (this.currentMarker) {
      this.map.removeLayer(this.currentMarker);
      this.currentMarker = null;
    }
  }

  override ngOnDestroy(): void {
    this.removeCoordinatesMarker();
    super.ngOnDestroy();
  }
}

export interface SelectSmesCoordsResult {
  event: LeafletMouseEvent;
  map: Map;
  clickedPoint: Point;
}

@Injectable()
export class SelectSmesCoordinatesMode extends SelectCoordsMode {
  override type = MapModes.SELECT_SMES_COORDS;

  public getCoords(): Observable<SelectSmesCoordsResult> {
    return this.mapClick$.pipe(
      map(({ event, map }) => ({
        event,
        map,
        clickedPoint: this.convertToClickedPoint(event),
      })),
    );
  }

  private convertToClickedPoint(event: LeafletMouseEvent): Point {
    return new Point(
      Math.round(event.containerPoint.x),
      Math.round(event.containerPoint.y),
    );
  }
}

interface SelectCoordsResult {
  latitude: string;
  longitude: string;
  easting: number;
  northing: number;
}

export interface SelectVicnamesCoordsResult extends SelectCoordsResult {
  event: LeafletMouseEvent;
  map: Map;
}

@Injectable()
export class SelectVicnamesCoordinatesMode extends SelectCoordsMode {
  override type = MapModes.SELECT_VICNAMES_COORDS;

  public getCoords(): Observable<SelectVicnamesCoordsResult> {
    return this.mapClick$.pipe(
      map(({ event, map }) => {
        const coordinates = this.convertEventToCoordinates(event);
        return coordinates ? { event, map, ...coordinates } : null;
      }),
      filter((result): result is SelectVicnamesCoordsResult => result !== null),
    );
  }

  private convertEventToCoordinates(
    event: LeafletMouseEvent,
  ): SelectCoordsResult | null {
    const pos = event.latlng;
    if (!pos) return null;

    const dmsPipe = new DMSPipe();
    const { lat, lng } = pos;

    const latitude = dmsPipe.transform(Math.abs(lat).toString().slice(0, 16));
    const longitude = dmsPipe.transform(lng.toString().slice(0, 16));

    const mgaPipe = new MGAPipe();
    const mgaResult = mgaPipe.transform(pos);
    const easting = mgaResult.point.x;
    const northing = mgaResult.point.y;

    return {
      latitude,
      longitude,
      easting,
      northing,
    };
  }
}
